import { useGetCallContactListQuery } from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import { CallListType } from "@bonliva-crm/shared";
import {
  AlertDialog,
  AlertDialogProps,
  MagnifyingGlassIcon,
  MultiGroupedSelect,
  sortSelectedFirst,
  useSnackbarsPipeline
} from "@bonliva-crm/ui";
import { Box, CircularProgress, makeStyles } from "@material-ui/core";
import { useMemo, useState } from "react";
import { useUserLeaveCallList } from "./hooks/user-leave-call-list";
import { CallOrContactList } from "./utils";
import { ActionMeta, MultiValue } from "react-select5";

type Props = {
  value: CallOrContactList[];
  onChange: (value: CallOrContactList[]) => void;
  onOptionEdit: (option: Option, type: CallListType) => void;
};

type Option = {
  label: string;
  value: CallOrContactList;
};

export function CallList(props: Props) {
  const classes = useStyles();
  const snack = useSnackbarsPipeline();
  const [dialogProps, setDialogProps] = useState<AlertDialogProps>({
    open: false,
    title: "",
    onClose: () => setDialogProps({ ...dialogProps, open: false }),
    onOk: () => {}
  });

  const callContactListQuery = useGetCallContactListQuery({
    fetchPolicy: "cache-and-network"
  });

  const callLists = useMemo(() => {
    return (callContactListQuery.data?.me.CallLists || []).filter(
      (callList) => !callList.deleted
    );
  }, [callContactListQuery.data?.me.CallLists]);

  const contactLists = useMemo(() => {
    return (callContactListQuery.data?.me.ContactLists || []).filter(
      (contactList) => !contactList.deleted
    );
  }, [callContactListQuery.data?.me.ContactLists]);

  const callListOptions = useMemo(() => {
    const options = callLists.map((callList) => ({
      label: callList.name,
      value: callList
    })) as Option[];

    return sortSelectedFirst({
      options,
      value: props.value,
      valueOptionEqualityFn: (value, option) => value._id === option.value._id
    });
  }, [callContactListQuery.data?.me.CallLists]);

  const contactListOptions = useMemo(() => {
    const options = contactLists.map((contactList) => ({
      label: contactList.name,
      value: contactList
    })) as Option[];

    return sortSelectedFirst({
      options,
      value: props.value,
      valueOptionEqualityFn: (value, option) => value._id === option.value._id
    });
  }, [callContactListQuery.data?.me.ContactLists]);

  const inputOptions = [
    {
      label: locale.CallLists,
      options: callListOptions as Option[]
    },
    {
      label: locale.ContactLists,
      options: contactListOptions as Option[]
    }
  ];

  const onCompleted = () => {
    snack.showSuccess(locale.Saved);
    setDialogProps({ ...dialogProps, open: false });
  };

  const onUserLeaveError = (e) => {
    console.error(e);
    snack.showError(
      locale.removeError(locale.User.toLowerCase(), locale.CallList.toLowerCase())
    );
  };

  const { userLeaveCallList } = useUserLeaveCallList({
    onCompleted,
    onError: onUserLeaveError
  });

  const userLeaveCallListDialog = (callList: CallOrContactList) => {
    const listToLeave = [...callLists, ...contactLists].find(
      (list) => list._id === callList._id
    );

    if (!listToLeave) {
      return;
    }

    setDialogProps({
      open: true,
      title:
        listToLeave.members.length === 1
          ? locale.CallListRemoveWithoutMembers
          : locale.CallListRemoveWithMembers,
      onClose: () => setDialogProps({ ...dialogProps, open: false }),
      onOk: () => userLeaveCallList(listToLeave)
    });
  };

  const onOptionEdit = (option: Option) => {
    const isCallList = callLists.some((callList) => callList._id === option.value._id);

    props.onOptionEdit(
      option,
      isCallList ? CallListType.CallList : CallListType.ContactList
    );
  };

  const onChange = (newValue: MultiValue<Option>, actionMeta: ActionMeta<Option>) => {
    if (["deselect-option", "remove-value", "pop-value"].includes(actionMeta.action)) {
      props.onChange(
        newValue
          .filter((option) => option.value._id !== actionMeta.option.value._id)
          .map((option) => option.value)
      );
    } else {
      props.onChange(newValue.map((option) => option.value));
    }
  };

  if (callContactListQuery.loading) {
    return (
      <Box
        flex={1}
        alignItems={"center"}
        display={"flex"}
        justifyContent={"center"}
        minHeight={400}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box flex={1} style={{ minHeight: 0, height: "auto" }}>
      <MultiGroupedSelect<Option>
        placeholder={
          <Box className={classes.placeholder}>
            <MagnifyingGlassIcon />
            {locale.Search}...
          </Box>
        }
        hidePlaceholderOnFocus={true}
        isMulti={true}
        options={inputOptions}
        isClearable={false}
        maxMenuHeight={600}
        controlShouldRenderValue={false}
        value={props.value.map((v) => ({ value: v, label: v.name }))}
        onChange={onChange}
        onOptionEdit={onOptionEdit}
        onOptionDelete={(option) => userLeaveCallListDialog(option.value)}
        isOptionSelected={(option) => props.value.some((v) => v._id === option.value._id)}
        menuIsOpen={true}
        styleProps={{
          control: { showBorder: true },
          menu: {
            position: "relative",
            maxHeight: "400px",
            height: "auto"
          },
          menuList: {
            maxHeight: "400px",
            overflow: "auto"
          }
        }}
      />

      <AlertDialog {...dialogProps} />
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  placeholder: {
    display: "flex",
    columnGap: "0.5rem",
    alignItems: "center"
  }
}));
